import {
  Component,
  Inject,
  InjectionToken,
  Injector,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { FilesTypeEnum } from '@app/ips/models/enum/files-type.enum';
import { DefaultUploadFileTypeAccept } from '@app/shared/constants';
import { FileUploadService } from '@app/shared/services/file-upload/file-upload.service';
import { LoadingService } from '@app/shared/services/loading.service';
import Utils from '@app/shared/utils/utils';
import { ToastrService } from 'ngx-toastr';
import { of, catchError, tap, switchMap, EMPTY, map } from 'rxjs';
import { ModalComponent } from '../modal/modal.component';
import { IGalleryService } from '@app/shared/interfaces';
import { Convert } from '@app/shared/utils/ConvertMimeTypes';

@Component({
  template: '',
})
export abstract class GalleryComponent implements OnInit, OnDestroy {
  idTrade!: string;
  midias: any[] = [];
  label = 'ADICIONAR MÍDIA';
  fileType = FilesTypeEnum;
  evidenceList: { url: string; title: string; type: FilesTypeEnum }[] = [];
  imageLoadingUrl = '/assets/imgs/no-image.png';
  acceptFiles = DefaultUploadFileTypeAccept;

  abstract generatePreSignedApiService: string;
  abstract registerUploadApiService: string;
  abstract source: InjectionToken<IGalleryService>;

  protected _requestService: IGalleryService;

  constructor(
    public dialog: MatDialog,
    private _route: ActivatedRoute,
    protected _toastrService: ToastrService,
    private _uploadService: FileUploadService,
    private _loading: LoadingService,
    private _injector: Injector
  ) {}

  ngOnDestroy(): void {
    this._loading.forceLoading = false;
  }

  ngOnInit() {
    this._requestService = this._injector.get<IGalleryService>(this.source);
    this.idTrade = this._route.snapshot.params.id;
    this._loading.forceLoading = true;
    this._getMidias(this.idTrade);
  }

  _getMidias(idTrade: string) {
    this._requestService
      .getGalleryByTradeId(idTrade)
      .pipe(
        map((responseItem) => {
          const images = responseItem.images || responseItem.stiImages || [];
          return images.map((img) => {
            const title = img.split('/').pop();
            return {
              url: img,
              title,
              type: this.getType(title),
            };
          });
        }),
        map((items) => (this.midias = items)),
        catchError(() => {
          this._toastrService.error('Erro ao tentar carregar as mídias.');
          return of([]);
        })
      )
      .subscribe((data) => (this.evidenceList = data));
  }

  getType(title: string) {
    const fileType = Utils.getFileTypeFrom(title);
    if (fileType.includes('image')) {
      return FilesTypeEnum.Image;
    } else if (fileType.includes('video')) {
      return FilesTypeEnum.Video;
    } else {
      return FilesTypeEnum.General;
    }
  }

  zoomImage(file: { url: string }): void {
    const modal = this.dialog.open(ModalComponent, {
      maxWidth: '100%',
      maxHeight: '100%',
      height: '100%',
      width: '100%',
      panelClass: 'full-screen-modal',
      data: this.midias,
    });
    modal.componentInstance.index = this.midias.findIndex((midia) => midia.url === file.url);
  }

  addMidia(event: any) {
    const file: File = event.target.files[0];

    if (!file) {
      return;
    }

    if (!Utils.isMimeTypeAllowed(file.type)) {
      this._toastrService.error(
        'Selecione apenas imagem, vídeo e pdf',
        'Arquivo selecionado inválido'
      );
      return;
    }

    const filename = `${this.idTrade}/${Utils.randomFilenameGenerator(
      'evidencia',
      Convert.getExtension(file.type)[0]
    )}`;
    const payload = { idTrade: this.idTrade, filename, contentType: file.type };
    this._uploadService
      .postGeneratePresignedUrlUpload(payload, this.generatePreSignedApiService)
      .pipe(
        tap(() => this._loading.show()),
        switchMap((response: { url: string }) =>
          this._uploadService.uploadFile(file, response.url)
        ),
        switchMap(() =>
          this._uploadService.updateTradeGallery(
            { idTrade: this.idTrade, filename },
            this.registerUploadApiService
          )
        ),
        tap(() => {
          this._getMidias(this.idTrade);
          this._loading.hide();
          this._toastrService.success('Upload enviado com sucesso.');
        }),
        catchError(() => {
          this._loading.hide();
          this._toastrService.error(
            'Não foi possível enviar a evidência. Por favor, tente novamente.'
          );
          return EMPTY;
        })
      )
      .subscribe();
  }
}
